import { Alert, Box, Button, FormLabel, Grid, Snackbar } from '@mui/material'
import { FC, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { Supplier } from '../../../../api/supplier/suppliers.types'
import { supplierGroupsAPI } from '../../../../api/supplierGroup/supplierGroupAPI'
import { supplierIntegrationDetailsResponse } from '../../../../api/supplierGroup/suppliergroup.types'
import { setSupplier } from '../../../../reducers/suppliersReducer'
import Form from '../../../RouteWizard/Form'
import { Select } from '../../../RouteWizard/Select'
import CredentialFields from './CredentialFields'
import defaultValues from './defaultValues'
import schema from './schema'
import { isSingletonSupplierGroup } from './supplierCredentialConfig'

interface IntegrationManagerProps {
  supplierGroupDetails?: supplierIntegrationDetailsResponse
  onCredentialsUpdated?: () => void
}

export const IntegrationManager: FC<IntegrationManagerProps> = ({
  supplierGroupDetails,
  onCredentialsUpdated,
}) => {
  const [formDisabled, setFormDisabled] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(false)
  const dispatch = useDispatch()
  const supplier: Supplier = useSelector(
    (state: any) => state.suppliers.supplier,
  )
  if (supplier === null) {
    return null
  }

  const supplierGroupList = useQuery({
    queryKey: ['getSupplierGroups'],
    queryFn: async () => supplierGroupsAPI.getSupplierGroups(),
    refetchOnWindowFocus: false,
  })

  const currentGroupId = supplier.supplierGroupId
  const isSingleton =
    currentGroupId !== undefined && isSingletonSupplierGroup(currentGroupId)

  const optss = supplierGroupList.data?.data.value
    .map(({ id: value, name: label }) => ({ value, label }))
    .filter(
      ({ value }) =>
        !isSingletonSupplierGroup(value) || value === currentGroupId,
    )

  const opts = optss?.reduce((supplierGroups: Record<number, string>, obj) => {
    if (obj.label !== undefined) {
      supplierGroups[obj.value] = obj.label
    }
    return supplierGroups
  }, {})

  const onSubmit = async (data: any) => {
    const trimmed = Object.fromEntries(
      Object.entries(data).map(([k, v]) => {
        if (typeof v !== 'string') return [k, v]
        const s = v.trim()
        return [k, k === 'endpoint' ? s.replace(/\/+$/, '') : s]
      }),
    ) as Record<string, any>

    if (!supplier.supplierGroupId) return

    const previousGroupId = Number(supplier.supplierGroupId)
    const selectedGroupId = Number(trimmed.supplierGroupId)
    const credentialPayload = {
      endPoint: trimmed.endpoint,
      dispatchReference: trimmed.dispatchReference,
      aPIKey: trimmed.apiKey,
      appKey: trimmed.appKey,
      appSecret: trimmed.appSecret,
      accountNumber: trimmed.accountNumber,
      unmanagedAccountNumber: trimmed.unmanagedAccountNumber,
    }

    setIsSaving(true)
    setError(false)
    try {
      if (selectedGroupId !== previousGroupId) {
        await supplierGroupsAPI.removeSupplierFromGroup(
          previousGroupId,
          supplier.id,
        )
        await supplierGroupsAPI.addSupplierToGroup(selectedGroupId, {
          supplierId: supplier.id,
          ...credentialPayload,
        })
        dispatch(setSupplier({ ...supplier, supplierGroupId: selectedGroupId }))
      } else {
        await supplierGroupsAPI.updateSupplierInGroup(
          selectedGroupId,
          supplier.id,
          credentialPayload,
        )
      }
      setFormDisabled(true)
      onCredentialsUpdated?.()
    } catch {
      setError(true)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Grid container justifyContent="flex-start">
      <Form
        schema={schema}
        onSubmit={onSubmit}
        defaultValues={{
          ...defaultValues,
          ...supplier,
          ...supplierGroupDetails,
        }}
        showControls={false}
      >
        <FormLabel>Integration Details</FormLabel>
        <Select
          name="supplierGroupId"
          label="Dispatch Platform"
          options={opts}
          disabled={formDisabled || isSingleton}
        />
        <CredentialFields formDisabled={formDisabled} />
        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          {formDisabled && !isSingleton && (
            <Button onClick={() => setFormDisabled(false)}>Edit</Button>
          )}
          {!formDisabled && (
            <>
              <Button onClick={() => setFormDisabled(true)} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                Save
              </Button>
            </>
          )}
        </Box>
      </Form>
      <Snackbar open={error} autoHideDuration={6000}>
        <Alert severity="error">Error updating supplier group details.</Alert>
      </Snackbar>
    </Grid>
  )
}

export default IntegrationManager
